import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';
import { CountObjectsComponent } from './CountObjectsComponent';
import { TenFrameComponent } from './TenFrameComponent';
import { NumberLineComponent } from './NumberLineComponent';
import { MatchingObjectsComponent } from './MatchingObjectsComponent';
import { FlashcardSetComponent } from './FlashcardSetComponent';
import { GridActivityComponent } from './GridActivityComponent';
import { useTrackingContext } from '../../contexts/TrackingContext';

interface QuizComponentProps {
  resource: Resource;
  component: UIComponent;
  onComplete?: (results: any[]) => void;
}

export const QuizComponent: React.FC<QuizComponentProps> = ({ resource, component, onComplete }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Get tracking context
  const { trackQuizResponse, trackAssessmentComplete, trackResourceInteraction } = useTrackingContext();

  // Get shuffle setting
  const shuffle = resource.shuffle !== undefined ? resource.shuffle : true;

  // Create and shuffle questions - use useMemo to prevent re-creation and re-shuffling on every render
  const displayQuestions = useMemo(() => {
    // Use custom questions from resource or fallback to defaults
    const customQuestions = resource.questions || [];
    
    // Create questions array with custom questions or fallback to defaults
    const questions = customQuestions.length > 0 ? customQuestions : [
      { prompt: "How many objects do you see?", answer: 8, choices: [6, 7, 8, 9, 10] },
      { prompt: "What number comes after 9?", answer: 10, choices: [8, 9, 10, 11, 12] },
      { prompt: "Count the dots", answer: 12, choices: [10, 11, 12, 13, 14] },
      { prompt: "What is 5 + 3?", answer: 8, choices: [6, 7, 8, 9, 10] },
      { prompt: "How many fingers am I holding up?", answer: 7, choices: [5, 6, 7, 8, 9] }
    ];

    // Shuffle if enabled
    return shuffle ? [...questions].sort(() => Math.random() - 0.5) : questions;
  }, [resource.questions, shuffle]);

  // Reset quiz state when resource changes
  useEffect(() => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setStartTime(Date.now());
    setQuestionStartTime(Date.now());
  }, [resource.id]);

  // Track question changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestion]);



  // Function to render resource component within a question
  const renderResourceComponent = (resourceComponent: any) => {
    if (!resourceComponent) return null;

    const mockComponent: UIComponent = {
      type: 'display',
      component_id: `quiz-${currentQuestion}-${resourceComponent.type}`,
      props: {}
    };

    const mockResource: Resource = {
      id: `quiz-resource-${currentQuestion}-${resourceComponent.type}`,
      type: resourceComponent.type,
      ...resourceComponent
    };

    switch (resourceComponent.type) {
      case 'count_objects_canvas':
        return (
          <div className="mb-4">
            <CountObjectsComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      case 'ten_frame':
        return (
          <div className="mb-4">
            <TenFrameComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      case 'number_line':
        return (
          <div className="mb-4">
            <NumberLineComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      case 'matching_objects':
        return (
          <div className="mb-4">
            <MatchingObjectsComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      case 'flashcard_set':
        return (
          <div className="mb-4">
            <FlashcardSetComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      case 'grid_activity':
        return (
          <div className="mb-4">
            <GridActivityComponent
              resource={mockResource}
              component={mockComponent}
              onAction={(action, data) => {
                // Track resource interaction
                trackResourceInteraction(
                  mockResource.id,
                  action,
                  data?.duration || 0
                ).catch(error => console.error('Error tracking resource interaction:', error));
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const handleAnswer = (answer: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);

    // Calculate time spent on current question
    const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);

    // Track quiz response
    const currentQ = displayQuestions[currentQuestion];
    const isCorrect = answer === currentQ?.answer;
    
    trackQuizResponse(
      `question_${currentQuestion + 1}`,
      answer,
      isCorrect,
      timeSpent
    ).catch(error => console.error('Error tracking quiz response:', error));

    if (currentQuestion < displayQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
    } else {
      // Quiz completed - track assessment completion
      const totalTimeSpent = Math.round((Date.now() - startTime) / 1000);
      const correctAnswers = newAnswers.filter((answer, index) => 
        answer === displayQuestions[index]?.answer
      ).length;
      const score = Math.round((correctAnswers / displayQuestions.length) * 100);

      trackAssessmentComplete(
        resource.id,
        score,
        totalTimeSpent
      ).catch(error => console.error('Error tracking assessment completion:', error));
      
      setShowResults(true);
      onComplete?.(newAnswers);
    }
  };

  if (showResults) {
    const correct = answers.filter((answer, index) => answer === displayQuestions[index]?.answer).length;
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">{t('quiz.quizResults')}</h3>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600 mb-2">
            {correct} / {displayQuestions.length} {t('quiz.correct')}
          </div>
          <div className="text-sm text-gray-600">
            {correct === displayQuestions.length ? t('quiz.perfectScore') : t('quiz.keepPracticing')}
          </div>
        </div>
      </div>
    );
  }

  const currentQ = displayQuestions[currentQuestion] || displayQuestions[0];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">{t('quiz.quickQuiz')}</h3>
        <p className="text-sm text-gray-600">
          {t('quiz.question')} {currentQuestion + 1} {t('quiz.of')} {displayQuestions.length}
        </p>
      </div>

      <div className="mb-4">
        <p className="text-lg font-medium text-gray-900 mb-3">
          {currentQ.prompt}
        </p>
        
        {/* Render resource component if present */}
        {currentQ.resource_component && (
          <div key={`resource-${currentQuestion}-${currentQ.resource_component.type}`}>
            {renderResourceComponent(currentQ.resource_component)}
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-2">
          {(currentQ.choices || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).map((num: number) => (
            <button
              key={num}
              onClick={() => handleAnswer(num)}
              className="p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-lg font-medium text-blue-800"
            >
              {num}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
